import { Divider, Header, IconQuestionSymbol, Space, Text } from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import { SubheadingStyled } from './styles/SubheadingStyled'

export interface IReportAccessDetailHeaderProps {
  isAllPrimaryCategories: boolean
  isAllSecondaryCategories: boolean
  secondaryCategoryName?: string
  primaryCategoryName?: string
}

/**
 * Presentational Feature Set subcomponent — the right-pane heading swaps by
 * mode: SecondaryCategory name + PrimaryCategory sub-line / "All
 * {SecondaryCategories}" / "All {PrimaryCategories}" — with per-mode helper
 * text and a help icon.
 */
export const ReportAccessDetailHeader = ({
  isAllPrimaryCategories,
  isAllSecondaryCategories,
  secondaryCategoryName,
  primaryCategoryName,
}: IReportAccessDetailHeaderProps) => {
  const intl = useProcurifyIntl()

  const heading = isAllPrimaryCategories
    ? intl.formatMessage(messages.allPrimaryCategoriesHeading)
    : isAllSecondaryCategories
      ? intl.formatMessage(messages.allSecondaryCategoriesHeading)
      : (secondaryCategoryName ?? '')

  const helperText = isAllPrimaryCategories
    ? intl.formatMessage(messages.allPrimaryCategoriesHelperText)
    : isAllSecondaryCategories
      ? intl.formatMessage(messages.allSecondaryCategoriesHelperText, {
          primaryCategoryName,
        })
      : intl.formatMessage(messages.singleHelperText, {
          secondaryCategoryName,
        })

  const subheading =
    !isAllPrimaryCategories && !isAllSecondaryCategories
      ? primaryCategoryName
      : undefined

  return (
    <Space size='small'>
      <Header as='h4'>{heading}</Header>
      {subheading && <SubheadingStyled>{subheading}</SubheadingStyled>}
      <Divider />
      <Space direction='horizontal' size='small' align='center'>
        <Text>{helperText}</Text>
        <IconQuestionSymbol
          size={16}
          ariaLabel={intl.formatMessage(messages.helpIconLabel)}
        />
      </Space>
    </Space>
  )
}

ReportAccessDetailHeader.displayName = 'ReportAccessDetailHeader'
